// Ilustração da página não encontrada. O Disk é um produto de ligação, então o
// 404 é a versão de tela do "o número chamado não existe": um celular que
// discou 404 e a onda da chamada que sai dele e se desfaz em pontos.
//
// Mesma mão das outras ilustrações (components/empty-illustrations,
// invitation-illustration): `currentColor` em três pesos e o acento em
// `text-primary`. Um único movimento: a onda se apaga uma vez, como a chamada
// que cai. Some com prefers-reduced-motion.
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

export function NotFoundIllustration({ className, ...props }: ComponentProps<"svg">) {
  return (
    <svg
      viewBox="0 0 280 200"
      fill="none"
      aria-hidden="true"
      className={cn("h-auto w-full max-w-[280px] text-muted-foreground", className)}
      {...props}
    >
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .nf-onda { animation: nf-cai 1.4s ease-out 0.3s both; }
          .nf-ponto { animation: nf-ponto 0.4s ease-out both; }
          .nf-ponto-1 { animation-delay: 1.1s; }
          .nf-ponto-2 { animation-delay: 1.25s; }
          .nf-ponto-3 { animation-delay: 1.4s; }
        }
        @keyframes nf-cai { from { stroke-dashoffset: 120; } to { stroke-dashoffset: 0; } }
        @keyframes nf-ponto { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      <ellipse cx="140" cy="186" rx="100" ry="6" className="fill-current opacity-[0.08]" />

      {/* Celular */}
      <rect x="54" y="24" width="84" height="152" rx="16" className="fill-background" />
      <rect x="54" y="24" width="84" height="152" rx="16" className="fill-current opacity-[0.06]" />
      <rect
        x="54"
        y="24"
        width="84"
        height="152"
        rx="16"
        className="stroke-current opacity-30"
        strokeWidth="2"
      />
      <rect x="84" y="32" width="24" height="5" rx="2.5" className="fill-current opacity-25" />

      {/* Número discado */}
      <text
        x="96"
        y="72"
        textAnchor="middle"
        className="fill-primary"
        style={{ font: "700 26px ui-sans-serif, system-ui, sans-serif", letterSpacing: "2px" }}
      >
        404
      </text>
      <rect x="76" y="80" width="40" height="4" rx="2" className="fill-current opacity-20" />

      {/* Teclado */}
      {[0, 1, 2].flatMap((row) =>
        [0, 1, 2].map((col) => (
          <circle
            key={`${row}-${col}`}
            cx={76 + col * 20}
            cy={100 + row * 17}
            r="6"
            className="fill-current opacity-[0.14]"
          />
        )),
      )}
      {/* Botão de encerrar a chamada */}
      <circle cx="96" cy="158" r="9" className="fill-primary" />
      <path
        d="M90.5 159.5 q5.5 -4 11 0"
        className="stroke-primary-foreground"
        strokeWidth="2.2"
        strokeLinecap="round"
      />

      {/* A onda da chamada, que se interrompe */}
      <path
        d="M146 100 q10 -18 20 0 t20 0 t20 0"
        className="nf-onda stroke-primary"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="120"
      />
      <circle cx="218" cy="100" r="3" className="nf-ponto nf-ponto-1 fill-primary opacity-70" />
      <circle cx="230" cy="100" r="2.6" className="nf-ponto nf-ponto-2 fill-primary opacity-45" />
      <circle cx="241" cy="100" r="2.2" className="nf-ponto nf-ponto-3 fill-primary opacity-25" />

      {/* Brilhos */}
      <path
        d="M36 60 l2.5 6 6 2.5 -6 2.5 -2.5 6 -2.5 -6 -6 -2.5 6 -2.5 Z"
        className="fill-primary opacity-60"
      />
      <circle cx="222" cy="58" r="3" className="fill-current opacity-25" />
      <circle cx="30" cy="140" r="2.5" className="fill-current opacity-20" />
    </svg>
  );
}
